import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  GridLegacy as Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  QueryStats as QueryStatsIcon,
} from '@mui/icons-material';

interface MpesaTransaction {
  id: number;
  checkout_request_id: string;
  school_id: number;
  school_name?: string;
  amount: number;
  phone_number: string;
  status: 'pending' | 'success' | 'failed' | 'cancelled';
  mpesa_receipt_number?: string;
  created_at: string;
}

export const MpesaTransactionManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<MpesaTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTransaction, setSelectedTransaction] = useState<MpesaTransaction | null>(null);
  const [transactionDialog, setTransactionDialog] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/mpesa/transactions');
      setTransactions(response.data);
    } catch (err) {
      setError('Network error while fetching transactions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <ErrorIcon />;
      case 'pending': return <ScheduleIcon />;
      default: return <PaymentIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('254')) {
      const localNumber = phone.substring(3);
      return `0${localNumber.substring(0, 2)} ${localNumber.substring(2, 5)} ${localNumber.substring(5)}`;
    }
    return phone;
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      transaction.checkout_request_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.phone_number.includes(searchTerm);
    
    return matchesStatus && matchesSearch;
  });

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">M-Pesa Transactions</Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchTransactions}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Filters */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Transactions Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Checkout ID</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Amount (KES)</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{transaction.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {transaction.checkout_request_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.school_name || `School ${transaction.school_id}`}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {transaction.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPhoneNumber(transaction.phone_number)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(transaction.status)}
                          label={transaction.status.toUpperCase()}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.mpesa_receipt_number ? (
                          <Typography variant="body2" fontFamily="monospace">
                            {transaction.mpesa_receipt_number}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(transaction.created_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setTransactionDialog(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog open={transactionDialog} onClose={() => setTransactionDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Transaction ID</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedTransaction.id}</Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary">Checkout Request ID</Typography>
                  <Typography variant="body1" fontFamily="monospace" sx={{ mb: 2 }}>
                    {selectedTransaction.checkout_request_id}
                  </Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary">School ID</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedTransaction.school_id}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                  <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
                    KES {selectedTransaction.amount.toLocaleString()}
                  </Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{formatPhoneNumber(selectedTransaction.phone_number)}</Typography>
                  
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedTransaction.status)}
                    label={selectedTransaction.status.toUpperCase()}
                    color={getStatusColor(selectedTransaction.status) as any}
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="subtitle2" color="textSecondary">Receipt Number</Typography>
                  <Typography variant="body1" fontFamily="monospace" sx={{ mb: 2 }}>
                    {selectedTransaction.mpesa_receipt_number || '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(selectedTransaction.created_at)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 